import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useRouteMatch } from 'react-router';
import { Link } from 'react-router-dom';

import { resolvePathname } from '@mssgme/helpers';
import { productsApi } from '@mssgme/lib';
import { Spinner } from '@mssgme/ui';
import { CardMoreButton } from '../../CardMoreButton';
import { CardProductsList } from '../CardProductsList';
import { ProductItem } from '../ProductItem';
import ScrollableHorizontalCardMenu from '../../ScrollableHorizontalCardMenu';
import styles from './CardProductsContainer.scss';

export function CardProductsContainer({ workspaceId, collections, animated, collectionsEnabled }) {
    const match = useRouteMatch();
    const defaultCollection = collections.items.find((col) => col.default);
    const fromCollection = defaultCollection?._id;
    const collectionsItems = collections.items.filter(
        (col) => !col.default && col.publishedProductsCount > 0 && col.published
    );
    const hasCollections = collectionsEnabled && collectionsItems.length > 0;
    const [{ hasNext, next, results }, setProducts] = useState({
        hasNext: false,
        next: null,
        publishedCount: 0,
        results: [],
    });
    const [isFetching, setIsFetching] = useState(false);
    const getProducts = async (next, collection, published, resetStore) => {
        setIsFetching(true);
        const productsStore = await productsApi.list(workspaceId, { next, collection, published });
        setProducts((prevProducts) => ({
            ...productsStore,
            results: resetStore ? productsStore.results : prevProducts.results.concat(productsStore.results),
        }));
        setIsFetching(false);
    };

    useEffect(() => {
        const initProducts = async () => {
            await getProducts(null, fromCollection, true, true);
        };

        void initProducts();
    }, [collections.lastRequest]);

    return (
        <div
            className={classNames(styles.root, styles.slideDown, {
                [styles.animated]: animated,
                [styles.hasCollections]: hasCollections,
            })}
        >
            {hasCollections && <ScrollableHorizontalCardMenu collectionsItems={collectionsItems} baseUrl={match.url} />}

            {results.length > 0 && (
                <div className={styles.productsContainer}>
                    <CardProductsList>
                        {results.map((item) => (
                            <ProductItem
                                key={item._id}
                                tag={Link}
                                product={item}
                                to={{
                                    pathname: resolvePathname(match.url, '/p/', item.slug),
                                    state: {
                                        fromCollection,
                                    },
                                }}
                            />
                        ))}
                    </CardProductsList>
                </div>
            )}
            {isFetching && <Spinner />}
            {hasNext && !isFetching && (
                <div className={styles.moreBtnContainer}>
                    <CardMoreButton
                        className={styles.moreBtn}
                        onClick={() => getProducts(next, fromCollection, true)}
                        disabled={isFetching}
                    />
                </div>
            )}
        </div>
    );
}

CardProductsContainer.propTypes = {
    workspaceId: PropTypes.string.isRequired,
    collections: PropTypes.object.isRequired,
    userUrl: PropTypes.string.isRequired,
    animated: PropTypes.bool,
    isAdmin: PropTypes.bool,
    collectionsEnabled: PropTypes.bool.isRequired,
};
